import { useViewCopyToAllProjects } from '@monorepo/logs/src/hooks/useViewCopyToAllProjects';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { ModalFormInstructions } from '@monorepo/shared/componentsV2/modals/contents/ModalFormInstructions';
import {
  StyledFormGroup,
  StyledInput,
  StyledSelect,
} from '@monorepo/shared/componentsV2/styled/form';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { getProjectSpecificLink } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameProject';
import { useHasOrgUpdatePermissionOrSuperAdmin } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useProjectsForUser } from '@monorepo/shared/hooks/useProjectsForUser';
import { isRequired } from '@monorepo/shared/utils/validators';
import * as Sentry from '@sentry/browser';
import { CopyEdpViewRequest, EdpViewResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useViewCopy } from '../../hooks/useViewCopy';

const COPY_TO_ALL_PROJECTS = '__COPY_TO_ALL_PROJECTS__';

type FormValues = {
  name: string;
  projectId: string;
};

function getError(values: FormValues) {
  // any error thrown in this function is unexpected - the UI should've protected against these
  const errMsg = `Tried to copy view without all required view request fields`;
  Sentry.captureException(errMsg, { extra: { values } });
  return new Error(errMsg);
}

function mapFormValuesToRequestDto(
  values: FormValues,
  existingView: EdpViewResponse,
): CopyEdpViewRequest {
  // this should always be defined by the time the form is getting submitted
  if (!values.name || !values.projectId) throw getError(values);
  return {
    copyName: values.name,
    copyProjectId: values.projectId,
    existingViewId: existingView.id,
  };
}

interface CopyViewModalProps {
  isLoading: boolean;
  onClose: () => void;
  existingView: EdpViewResponse;
}

export function CopyViewModal({
  isLoading,
  onClose,
  existingView,
}: CopyViewModalProps) {
  const { organizationId, projectId } = useParams();
  const navigate = useNavigate();
  const [singleProjectCopier] = useViewCopy({ config: { throwOnError: true } });
  const [allProjectsCopier] = useViewCopyToAllProjects({
    config: { throwOnError: true },
  });
  const canCopyToAllProjects =
    useHasOrgUpdatePermissionOrSuperAdmin(organizationId);

  const { projects, isLoading: isLoadingProjects } = useProjectsForUser();

  const siteOptions = useMemo(() => {
    const filteredProjects =
      projects?.filter((p) => p.organization_id === organizationId) || [];
    const sortedProjects = filteredProjects.sort((p1, p2) =>
      p1.name.localeCompare(p2.name),
    );
    const sortedOptions = sortedProjects.map((p) => ({
      label: p.name,
      value: p.id,
    }));
    // We allow copying to the same project, but when copying to all projects, we ignore the current project.
    // If there are only 2 projects in the dropdown, one of them is current project,
    // so it doesn't make sense to copy to all projects since we gonna copy either to one project of none.
    // A user can just select that option themselves and copy to a single project.
    if (!canCopyToAllProjects || sortedOptions.length < 3) {
      return sortedOptions;
    }
    return [
      {
        label: 'All sites (excludes current site)',
        value: COPY_TO_ALL_PROJECTS,
      },
      ...sortedOptions,
    ];
  }, [canCopyToAllProjects, organizationId, projects]);

  const { showUserFriendlyErrorToast, success: showSuccess } = useToast();
  const onSubmit = useCallback(
    async (values: FormValues) => {
      const isCopyingToAllProjects = values.projectId === COPY_TO_ALL_PROJECTS;
      try {
        if (!organizationId || !projectId) {
          throw new Error(
            `Couldn't create Report: This page is missing an organization id or site id.`,
          );
        }

        const requestDto = mapFormValuesToRequestDto(values, existingView);

        const copier = isCopyingToAllProjects
          ? allProjectsCopier
          : singleProjectCopier;
        const createdViewCopy = await copier({
          organizationId,
          projectId,
          copyViewOptions: requestDto,
        });
        const successMsg = isCopyingToAllProjects
          ? 'This could take some time. You can close the window and we will send you an email when the Report is copied to all sites.'
          : `${values.name} has been created.`;
        showSuccess(successMsg, { autoHideDuration: 20 * 1000 });
        onClose();

        if (createdViewCopy && 'id' in createdViewCopy) {
          const projectAllViewsLink = getProjectSpecificLink({
            projectId: values.projectId,
            organizationId,
            pathEnd: '/logs/views',
          });
          navigate(`${projectAllViewsLink}/${createdViewCopy.id}`);
        }
      } catch (error) {
        const errorMsg = isCopyingToAllProjects
          ? `Unable to copy ${values.name} to all sites.`
          : `Unable to save ${values.name}.`;
        showUserFriendlyErrorToast(error, errorMsg, { dontAutoHide: true });
      }
    },
    [
      allProjectsCopier,
      existingView,
      navigate,
      onClose,
      organizationId,
      projectId,
      showSuccess,
      showUserFriendlyErrorToast,
      singleProjectCopier,
    ],
  );

  if (isLoading || isLoadingProjects) {
    return <Loading />;
  }

  return (
    <Form<FormValues>
      initialValues={{
        name: `Copy of ${existingView.name}`,
      }}
      onSubmit={onSubmit}
      subscription={{
        invalid: true,
        submitting: true,
        values: true,
      }}
    >
      {({ handleSubmit, submitting, values }) => (
        <BaseModal
          buttons={
            <>
              <Button color="primary" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={submitting || !values.name || !values.projectId}
                onClick={handleSubmit}
                type="submit"
                variant="contained"
              >
                Save
              </Button>
            </>
          }
          onClose={onClose}
          open
          showCloseButton
          title="Copy Report"
        >
          <ModalFormInstructions>
            Name your Report and choose which site you&#39;d like to copy to.
          </ModalFormInstructions>
          <StyledFormGroup row>
            <StyledInput<NonNullable<FormValues['name']>>
              label="Report Name"
              name="name"
              placeholder="Name"
              required
              validate={isRequired}
            />
          </StyledFormGroup>
          <StyledFormGroup row>
            <StyledSelect<NonNullable<FormValues['projectId']>>
              label="Site"
              name="projectId"
              required
              options={siteOptions}
            />
          </StyledFormGroup>
        </BaseModal>
      )}
    </Form>
  );
}
